import * as WebBrowser from 'expo-web-browser';
import * as Linking from 'expo-linking';
import { supabase } from './supabase';

// Retour OAuth LinkedIn dans l'app (chemin distinct de auth/callback Google et auth/confirm email).
// Capté inline par openAuthSessionAsync → useAuthDeepLink (scopé sur 'auth/confirm') ne le voit pas.
const APP_RETURN = 'hormeia://auth/linkedin';

export type LinkedinOutcome = 'connected' | 'cancelled' | 'unavailable' | 'error';

export interface LinkedinStatus {
  connected: boolean;
  expired: boolean;
  expiresAt: string | null;
}

// Statut de connexion (jamais le token). L'edge dérive l'utilisateur du JWT.
export async function getLinkedinStatus(): Promise<LinkedinStatus> {
  const { data, error } = await supabase.functions.invoke<LinkedinStatus & { error?: string }>('linkedin-oauth', {
    body: { action: 'status' },
  });
  if (error || !data || data.error) throw new Error('Statut LinkedIn indisponible.');
  return { connected: !!data.connected, expired: !!data.expired, expiresAt: data.expiresAt ?? null };
}

// Retrait du consentement (RGPD) : supprime le token stocké côté serveur.
export async function disconnectLinkedin(): Promise<void> {
  const { data, error } = await supabase.functions.invoke<{ disconnected?: boolean; error?: string }>('linkedin-oauth', {
    body: { action: 'disconnect' },
  });
  if (error || !data?.disconnected) throw new Error('Déconnexion LinkedIn impossible.');
}

/**
 * Lance le flux OAuth : l'edge renvoie l'URL d'autorisation (state signé) ET ce state, que l'on
 * GARDE ici. Le rebond serveur échange le code puis revient dans l'app avec un simple statut
 * (jamais le code ni le token) :
 *  - `pending` : le jeton attend sa confirmation. On rappelle l'edge avec le JWT du compte, le state
 *    gardé et le code du lien de retour ; le serveur ne rattache que si les trois concordent. Ainsi
 *    une URL d'autorisation émise pour ce compte mais ouverte ailleurs ne peut rien y rattacher :
 *    le lien de retour part vers l'appareil qui a autorisé, qui n'a pas le state.
 *  - `connected` : serveur d'avant la confirmation, qui a rattaché directement.
 */
export async function connectLinkedin(): Promise<LinkedinOutcome> {
  // `confirmation: true` déclare que ce client sait confirmer ; un serveur qui l'ignore rattache
  // directement et répond `connected`, ce qui reste géré plus bas.
  const { data, error } = await supabase.functions.invoke<{ authUrl?: string; state?: string; error?: string }>(
    'linkedin-oauth',
    { body: { action: 'start', confirmation: true } },
  );
  if (error || !data?.authUrl) return 'error';

  const result = await WebBrowser.openAuthSessionAsync(data.authUrl, APP_RETURN);
  if (result.type !== 'success') return 'cancelled'; // fermeture / annulation

  const { queryParams } = Linking.parse(result.url);
  const status = queryParams?.status;
  if (status === 'connected') return 'connected';
  if (status === 'cancelled') return 'cancelled';
  if (status === 'pending') {
    const code = queryParams?.confirm;
    if (typeof code !== 'string' || !code || !data.state) return 'error';
    return confirmerLiaison(data.state, code);
  }
  // Échec d'échange = le plus souvent membre hors UE/EEE/Suisse (scope DMA non octroyé).
  if (queryParams?.reason === 'exchange') return 'unavailable';
  return 'error';
}

async function confirmerLiaison(state: string, code: string): Promise<LinkedinOutcome> {
  const { data, error } = await supabase.functions.invoke<{ connected?: boolean; reason?: string }>('linkedin-oauth', {
    body: { action: 'confirm', state, code },
  });
  if (error || !data?.connected) return 'error';
  return 'connected';
}
